import * as React from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import {
  IModalManagerContext,
  ModalManagerContext,
} from "src/components/Modal/ModalManager.context";
import { isIFrame } from "src/helpers";
import styled, { css } from "styled-components";
import { useEventListener, useWindow } from "src/hooks";
import { MediaQueries } from "src/constants";
import { animated, useSpring } from "react-spring";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { Location } from "history";

export type ModalManagerProps = React.HTMLProps<HTMLDivElement> &
  IModalManagerContext;

const ModalManagerEl = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  height: ${(p) => p.height}px;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: ${(p) => p.theme.layout.zIndex.modal};

  ${MediaQueries.screen.medium.minWidth} {
    align-items: center;
    justify-content: center;
  }

  ${MediaQueries.screen.medium.maxWidth} {
    justify-content: flex-end;
  }
`;

const ModalBackdropEl = styled(animated.div)`
  background: ${(p) => p.theme.colors.gradients.backdrop};
  height: 100%;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;

  ${isIFrame() &&
  css`
    background: rgba(0, 0, 0, 0.35);
  `}
`;

const ModalBackdrop = ({ onClick }: any) => {
  const animatedProps = useSpring({
    config: { duration: 120 },
    opacity: 1,
    from: {
      opacity: 0,
    },
  });

  return (
    <ModalBackdropEl
      id="asp-modal-backdrop"
      onClick={onClick}
      style={animatedProps}
    />
  );
};

export const ModalManager = observer((props: ModalManagerProps) => {
  const {
    children,
    className,
    showModal,
    prompt = false,
    onClose,
    fullScreen,
    modalSize,
    scrollable,
  } = props;

  const { innerHeight } = useWindow();

  const onKeyUp = React.useCallback(
    (event: React.KeyboardEvent) => {
      // on ESCAPE key pressed
      if (event.keyCode === 27 && !prompt) {
        onClose();
      }
    },
    [onClose, prompt]
  );

  useEventListener("keyup", onKeyUp);

  if (!showModal) {
    return null;
  }

  const onBackdropClick = () => {
    if (!prompt) {
      onClose();
    }
  };

  const currentContext = {
    showModal,
    fullScreen,
    onClose,
    prompt,
    modalSize,
    scrollable,
  };

  return createPortal(
    <ModalManagerEl className={className} height={innerHeight}>
      <ModalBackdrop onClick={onBackdropClick} />
      <ModalManagerContext.Provider value={currentContext}>
        {children}
      </ModalManagerContext.Provider>
    </ModalManagerEl>,
    document.getElementById("root")!
  );
});

export const RouterModalManager = (
  props: Partial<ModalManagerProps> & { hash: string }
) => {
  const { hash, ...other } = props;

  const { goBack, location, listen } = useHistory();

  const [showModal, setShowModal] = useState(location.hash === hash);

  const routeChangeListener = (location: Location) => {
    setShowModal(location.hash === hash);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => listen(routeChangeListener), []);

  return <ModalManager {...other} onClose={goBack} showModal={showModal} />;
};
